import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DashNavbar from "./DashNavbar";
import Sidebar from "./Sidebar";
import Select from "react-select";
import { Modal, Pagination, Form, Spinner, Badge, Card, Table, InputGroup, Button, Row, Col } from "react-bootstrap";
import { FaSearch, FaCalendarPlus, FaUsers, FaTrash, FaPlus, FaVideo, FaMapMarkerAlt } from "react-icons/fa";
import { FadeLoader } from "react-spinners";
import "../css/InterviewSchedule.css";

const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/";

const reactSelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: "10px",
    padding: "2px",
    border: "1px solid #dee2e6",
    boxShadow: "none",
    "&:hover": { border: "1px solid #0d6efd" }
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? "#0d6efd" : isFocused ? "#e9ecef" : "white",
    color: isSelected ? "white" : "black",
    cursor: "pointer"
  })
};

const getCandidateFullName = (applicant) => {
  if (!applicant) return "N/A";
  const { first_name = "", middle_name = "", last_name = "" } = applicant;
  return `${first_name} ${middle_name} ${last_name}`.replace(/\s+/g, " ").trim() || "N/A";
};

export default function InterviewSchedule() {
  const [applicantData, setApplicantData] = useState([]);
  const [interviewData, setInterviewData] = useState([]);
  const [filteredInterviewData, setFilteredInterviewData] = useState([]);
  const [interviewersData, setInterviewersData] = useState([]);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [openingPostOptions, setOpeningPostOptions] = useState([]);
  const [interviewerOptions, setInterviewerOptions] = useState([]);
  const [candidateJobs, setCandidateJobs] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showUserListModal, setShowUserListModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userPage, setUserPage] = useState(1);
  const [userItemsPerPage, setUserItemsPerPage] = useState(10);

  const getPageOptions = () => {
    const total = filteredInterviewData.length;
    if (total <= 5) return [5];
    if (total <= 10) return [5, 10];
    if (total <= 25) return [5, 10, 25];
    if (total <= 50) return [5, 10, 25, 50];
    return [5, 10, 25, 50, 100];
  };

  const [formData, setFormData] = useState({
    candidate: "", interviewer: "", additionalMembers: [], meetLink: "", date: "", time: "", candidateId: null,
    jobTitle: "", opening_post: "", mode: "Online", location: "", interviewer_contact_number: "",
  });

  const candidateOptions = applicantData
    .sort((a, b) => getCandidateFullName(a).localeCompare(getCandidateFullName(b)))
    .map((candidate) => {
      const fullName = getCandidateFullName(candidate);
      return { value: fullName, label: `${fullName} (ID: ${candidate.id})`, id: candidate.id };
    });

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [applicantRes, interviewsRes, interviewersRes] = await Promise.all([
          axios.get(`${API_BASE_URL}applicants_alt/`),
          axios.get(`${API_BASE_URL}schedule-interview/`),
          axios.get(`${API_BASE_URL}get_interviewers/`),
        ]);

        setApplicantData(applicantRes.data);
        const interviews = Array.isArray(interviewsRes.data) ? interviewsRes.data : (interviewsRes.data ? [interviewsRes.data] : []);
        setInterviewData(interviews);
        setFilteredInterviewData(interviews);

        setInterviewersData(interviewersRes.data);
        setInterviewerOptions(interviewersRes.data.map(i => ({ value: i.name, label: i.name })));
      } catch (err) {
        console.error("Error fetching initial data:", err);
        Swal.fire({ icon: "error", title: "Load Failed", text: "Could not fetch initial data." });
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData.candidateId) {
      fetchCandidateJobDetails(formData.candidateId);
    } else {
      setOpeningPostOptions([]);
      setJobTitleOptions([]);
      setCandidateJobs([]);
    }
  }, [formData.candidateId]);

  useEffect(() => {
    if (formData.opening_post && candidateJobs.length > 0) {
      const filteredTitles = candidateJobs
        .filter(j => j.opening_post === formData.opening_post)
        .map(j => j.job_title);
      setJobTitleOptions([...new Set(filteredTitles)].map(t => ({ value: t, label: t })));
    } else {
      setJobTitleOptions([]);
    }
  }, [formData.opening_post, candidateJobs]);

  const fetchCandidateJobDetails = async (candidateId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}applicant/job-title/${candidateId}/`);
      const jobs = Array.isArray(res.data) ? res.data : [];
      setCandidateJobs(jobs);
      const uniquePosts = [...new Set(jobs.map(j => j.opening_post).filter(p => p))];
      setOpeningPostOptions(uniquePosts.map(p => ({ value: p, label: p.replace(/_/g, ' ') })));
    } catch (e) {
      console.error("Error fetching jobs:", e);
    }
  };

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      setFilteredInterviewData(interviewData);
      setCurrentPage(1);
      return;
    }

    const filtered = interviewData.filter((i) => {
      const candidateName = (i.candidate || "").toLowerCase();
      const interviewerName = (i.interviewer || "").toLowerCase();
      const jobTitle = (i.job_title || "").toLowerCase();
      const dateStr = (i.date || "").toLowerCase();

      return (
        candidateName.includes(query) ||
        interviewerName.includes(query) ||
        jobTitle.includes(query) ||
        dateStr.includes(query)
      );
    });
    setFilteredInterviewData(filtered);
    setCurrentPage(1);
  }, [interviewData, searchQuery]);

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    const updatedData = { ...formData, [name]: value };

    if (name === "candidate") {
      updatedData.candidateId = selectedOption ? selectedOption.id : null;
      updatedData.opening_post = "";
      updatedData.jobTitle = "";
    }

    if (name === "interviewer") {
      const interviewer = interviewersData.find(i => i.name === value);
      updatedData.interviewer_contact_number = interviewer ? interviewer.phone || "" : "";
    }

    if (name === "opening_post") {
      updatedData.jobTitle = "";
    }

    setFormData(updatedData);
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullSelectedCandidate = applicantData.find(c => c.id === formData.candidateId);
      const selectedInterviewer = interviewersData.find(i => i.name === formData.interviewer);

      const dataToSend = {
        ...formData,
        candidate_email: fullSelectedCandidate?.email, // Keeping original just in case
        interviewer_email: selectedInterviewer?.email,
        candidateEmail: fullSelectedCandidate?.email, // Added for backend validation
        interviewerEmail: selectedInterviewer?.email, // Added for backend validation
        opening_post_id: formData.opening_post, // Using opening_post as ID if needed, or adjust based on backend
      };

      await axios.post(`${API_BASE_URL}schedule-interview/`, dataToSend);
      const res = await axios.get(`${API_BASE_URL}schedule-interview/`);
      setInterviewData(res.data);
      setShowModal(false);
      Swal.fire({ icon: "success", title: "Success", text: "Interview scheduled successfully!", timer: 2000, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.error || "Failed to schedule interview." });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    const [h, m] = time.split(":");
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  // Pagination Logic for Main Table
  const totalItems = filteredInterviewData.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredInterviewData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const firstIndex = totalItems === 0 ? 0 : indexOfFirstItem + 1;
  const lastIndex = Math.min(indexOfLastItem, totalItems);

  // Pagination Logic for Candidate Modal
  const totalUserRows = applicantData.length;
  const userIndexOfLastItem = userPage * userItemsPerPage;
  const userIndexOfFirstItem = userIndexOfLastItem - userItemsPerPage;
  const currentUserItems = applicantData.slice(userIndexOfFirstItem, userIndexOfLastItem);
  const totalUserPages = Math.ceil(totalUserRows / userItemsPerPage) || 1;

  return (
    <>
      <DashNavbar />
      <div className="wrapper bg-light">
        <Sidebar />
        <main className="main px-4 py-4">
          <div className="container-fluid">
            <div className="card border-0 rounded-4 shadow-sm p-4 bg-white mb-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                <h2 className="fw-bold mb-0 text-primary">Interview Schedule</h2>
                <div className="d-flex gap-2">
                  <Button variant="success" className="rounded-pill px-4 shadow-sm" onClick={() => setShowModal(true)}>
                    <FaCalendarPlus className="me-2" /> Schedule
                  </Button>
                  <Button variant="primary" className="rounded-pill px-4 shadow-sm" onClick={() => setShowUserListModal(true)}>
                    <FaUsers className="me-2" /> Users
                  </Button>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-6 col-lg-4">
                  <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                    <InputGroup.Text className="bg-white border-0 ps-3">
                      <FaSearch className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      className="border-0 shadow-none py-2"
                      placeholder="Search schedule..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </div>

              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <FadeLoader color="#0d6efd" />
                </div>
              ) : (
                <div className="table-responsive rounded-3 border shadow-sm">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-primary text-white text-uppercase small fw-bold">
                      <tr>
                        <th className="py-3 ps-3">Sr. No</th>
                        <th className="py-3">Candidate</th>
                        <th className="py-3">Position</th>
                        <th className="py-3">Interviewer</th>
                        <th className="py-3">Date & Time</th>
                        <th className="py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? currentItems.map((i, idx) => (
                        <tr key={idx}>
                          <td className="ps-3 text-muted">{indexOfFirstItem + idx + 1}</td>
                          <td>
                            <div className="fw-bold">{i.candidate}</div>
                            <small className="text-muted">ID: {i.candidate_id || "N/A"}</small>
                          </td>
                          <td>
                            <div className="fw-medium">{i.job_title || "N/A"}</div>
                            <Badge bg="light" text="dark" className="border small fst-normal">
                              {i.opening_post?.replace(/_/g, ' ') || "N/A"}
                            </Badge>
                          </td>
                          <td>{i.interviewer}</td>
                          <td>
                            <div className="text-primary fw-bold">{i.date ? new Date(i.date).toLocaleDateString('en-GB') : "N/A"}</div>
                            <small className="text-muted">{formatTime(i.time)}</small>
                          </td>
                          <td className="text-center">
                            <Badge bg={i.mode === 'Online' ? 'info' : 'secondary'} className="rounded-pill px-3">
                              {i.mode === 'Online' ? <FaVideo className="me-1" /> : <FaMapMarkerAlt className="me-1" />}
                              {i.mode}
                            </Badge>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="6" className="text-center py-5 text-muted">No scheduled interviews found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Standardized Professional Pagination Footer */}
              {(totalItems > 0) && (
                <div className="d-flex justify-content-between align-items-center mt-4 border-top pt-3 flex-wrap gap-3">
                  <div className="text-muted small">
                    Showing <strong>{firstIndex}</strong> to <strong>{lastIndex}</strong> of <strong>{totalItems}</strong> records
                  </div>

                  <div className="d-flex align-items-center gap-4">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">Rows per page:</span>
                      <Form.Select
                        size="sm"
                        className="shadow-none border-light-subtle"
                        style={{ width: '70px', fontSize: '13px', borderRadius: '6px' }}
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        {getPageOptions().map(val => (
                          <option key={val} value={val}>{val}</option>
                        ))}
                      </Form.Select>
                    </div>

                    <div className="d-flex align-items-center gap-0">
                      {/* Vertical divider */}
                      <div className="border-start mx-1" style={{ height: '24px', borderColor: '#e2e8f0' }}></div>

                      {/* Page info */}
                      <span className="text-muted mx-3">
                        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                      </span>

                      {/* Vertical divider */}
                      <div className="border-start mx-1 me-3" style={{ height: '24px', borderColor: '#e2e8f0' }}></div>

                      {/* Navigation buttons */}
                      <div className="d-flex align-items-center gap-1">
                        <button
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                          style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                          title="First page"
                        >
                          <span style={{ fontSize: '14px', lineHeight: 1 }}>«</span>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                          style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          title="Previous page"
                        >
                          <span style={{ fontSize: '14px', lineHeight: 1 }}>‹</span>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                          style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          title="Next page"
                        >
                          <span style={{ fontSize: '14px', lineHeight: 1 }}>›</span>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                          style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                          title="Last page"
                        >
                          <span style={{ fontSize: '14px', lineHeight: 1 }}>»</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Schedule Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className="premium-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-primary">Schedule New Interview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Candidate</Form.Label>
                <Select options={candidateOptions} styles={reactSelectStyles} value={candidateOptions.find(o => o.id === formData.candidateId)} onChange={(val) => handleSelectChange("candidate", val)} placeholder="Search candidate..." isSearchable isClearable />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Interviewer</Form.Label>
                <Select options={interviewerOptions} styles={reactSelectStyles} value={interviewerOptions.find(o => o.value === formData.interviewer)} onChange={(val) => handleSelectChange("interviewer", val)} placeholder="Search interviewer..." isSearchable isClearable />
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Opening Post</Form.Label>
                <Select options={openingPostOptions} styles={reactSelectStyles} value={openingPostOptions.find(o => o.value === formData.opening_post)} onChange={(val) => handleSelectChange("opening_post", val)} isDisabled={!formData.candidateId} placeholder="Select post..." />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Job Title</Form.Label>
                <Select options={jobTitleOptions} styles={reactSelectStyles} value={jobTitleOptions.find(o => o.value === formData.jobTitle)} isDisabled={!formData.opening_post} onChange={(val) => handleSelectChange("jobTitle", val)} placeholder="Select title..." />
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Interview Mode</Form.Label>
                <Form.Select className="rounded-3 shadow-sm border-light" value={formData.mode} onChange={(e) => setFormData({ ...formData, mode: e.target.value })}>
                  <option value="Online">Online / Video Call</option>
                  <option value="Offline">Offline / In-Person</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Meeting Info</Form.Label>
                <Form.Control
                  className="rounded-3 shadow-sm border-light"
                  name={formData.mode === 'Online' ? "meetLink" : "location"}
                  value={formData.mode === 'Online' ? formData.meetLink : formData.location}
                  onChange={handleFormInputChange}
                  placeholder={formData.mode === 'Online' ? "Video call link..." : "Physical address..."}
                  required
                />
              </Col>
            </Row>
            <Row className="g-3 mb-4">
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Date</Form.Label>
                <Form.Control type="date" name="date" className="rounded-3 shadow-sm border-light" value={formData.date} onChange={handleFormInputChange} min={new Date().toISOString().split("T")[0]} required />
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold small text-uppercase text-muted">Time</Form.Label>
                <Form.Control type="time" name="time" className="rounded-3 shadow-sm border-light" value={formData.time} onChange={handleFormInputChange} required />
              </Col>
            </Row>
            <div className="d-grid">
              <Button variant="primary" type="submit" size="lg" className="rounded-pill fw-bold shadow-sm" disabled={loading}>
                {loading ? <Spinner size="sm" animation="border" className="me-2" /> : <FaCalendarPlus className="me-2" />}
                {loading ? "Scheduling..." : "Schedule Interview"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* User List Modal */}
      <Modal show={showUserListModal} onHide={() => setShowUserListModal(false)} centered size="lg" scrollable={true}>
        <Modal.Header closeButton className="premium-modal border-0 bg-light-subtle">
          <Modal.Title className="fw-bold">Candidate Directory</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-4">
          <div className="table-responsive rounded-3 shadow-sm border bg-white">
            <Table hover className="align-middle border-0 mb-0">
              <thead className="bg-light text-secondary small text-uppercase fw-bold border-bottom">
                <tr>
                  <th className="py-3 ps-3 border-0">Sr. No</th>
                  <th className="border-0">Name</th>
                  <th className="border-0">Email</th>
                  <th className="pe-3 border-0">ID</th>
                </tr>
              </thead>
              <tbody className="border-0">
                {currentUserItems.map((u, idx) => (
                  <tr key={idx} className="border-bottom-0">
                    <td className="ps-3 text-muted border-0">{userIndexOfFirstItem + idx + 1}</td>
                    <td className="fw-bold border-0 text-dark">{getCandidateFullName(u)}</td>
                    <td className="border-0 text-secondary">{u.email}</td>
                    <td className="pe-3 border-0 text-muted">{u.id}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-4 border-top pt-3 flex-wrap gap-3" style={{ fontSize: '13px' }}>
            <div className="text-muted">
              Showing <strong>{currentUserItems.length > 0 ? userIndexOfFirstItem + 1 : 0}</strong> to <strong>{Math.min(userIndexOfFirstItem + currentUserItems.length, applicantData.length)}</strong> of <strong>{applicantData.length}</strong> results
            </div>

            <div className="d-flex align-items-center gap-0">
              {/* Rows per page selection */}
              <div className="d-flex align-items-center gap-2 me-3">
                <span className="text-muted">Rows per page:</span>
                <Form.Select
                  size="sm"
                  className="py-1 px-2 border-light shadow-sm bg-white"
                  style={{ width: 'auto', fontSize: '12px', borderRadius: '6px' }}
                  value={userItemsPerPage}
                  onChange={(e) => {
                    setUserItemsPerPage(Number(e.target.value));
                    setUserPage(1);
                  }}
                >
                  {[5, 10, 20, 30, 40, 50, 100].map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Select>
              </div>

              {/* Vertical divider */}
              <div className="border-start mx-1" style={{ height: '24px', borderColor: '#e2e8f0' }}></div>

              {/* Page info */}
              <span className="text-muted mx-3">
                Page <strong>{userPage}</strong> of <strong>{totalUserPages || 1}</strong>
              </span>

              {/* Vertical divider */}
              <div className="border-start mx-1 me-3" style={{ height: '24px', borderColor: '#e2e8f0' }}></div>

              {/* Navigation buttons */}
              <div className="d-flex align-items-center gap-1">
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                  style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                  disabled={userPage === 1}
                  onClick={() => setUserPage(1)}
                  title="First page"
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }}>«</span>
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                  style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                  disabled={userPage === 1}
                  onClick={() => setUserPage(prev => Math.max(prev - 1, 1))}
                  title="Previous page"
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }}>‹</span>
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                  style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                  disabled={userPage === totalUserPages || totalUserPages === 0}
                  onClick={() => setUserPage(prev => Math.min(prev + 1, totalUserPages))}
                  title="Next page"
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }}>›</span>
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                  style={{ width: '32px', height: '32px', borderRadius: '6px', padding: 0 }}
                  disabled={userPage === totalUserPages || totalUserPages === 0}
                  onClick={() => setUserPage(totalUserPages)}
                  title="Last page"
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }}>»</span>
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}